import {
  EmailEventTypes,
  UserEventTypes,
  EmailAddressWSPayload,
  UserUpdatedEventPayload,
} from '@act/shared/socket-events';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { WebsocketEventActions } from '../types/websocketTypes';
import { buildMemberExtraReducers } from './memberSliceExtraReducers.util';
import { registerMemberEffects } from './memberSliceEffects.util';
import {
  initialMemberState,
  memberEntityAdapter,
  paginatedMemberEntityAdapter,
} from './memberSliceState.util';

export * from './memberSliceActions.thunk';
export * from './memberSliceSelectors.util';

export const memberSlice = createSlice({
  name: 'member',
  initialState: initialMemberState,
  reducers: {
    emailDeleted: (state, action: PayloadAction<EmailAddressWSPayload>) => {
      const { emailAddress, memberId } = action.payload;
      const member = state.members.entities[memberId];
      if (!member) {
        return state;
      }

      const emailAddressesMinusDeleted = member.emailAddresses.filter(
        ea => ea.id !== emailAddress.id
      );

      state.members.entities[memberId] = {
        ...member,
        emailAddresses: [...emailAddressesMinusDeleted],
      };

      return state;
    },
    userUpdated: (state, action: PayloadAction<UserUpdatedEventPayload>) => {
      const patch = action.payload.user;
      const id = patch.id;
      const originalUser = state.members.entities[id];
      const merged = {
        ...(originalUser ?? {}),
        ...patch,
        memberPermissions: originalUser?.memberPermissions ?? [],
        userPermissions: originalUser?.userPermissions ?? [],
      };
      memberEntityAdapter.upsertOne(state.members, merged);

      const paginatedOriginal = state.paginatedMembers.entities[id];
      if (paginatedOriginal !== undefined) {
        paginatedMemberEntityAdapter.upsertOne(state.paginatedMembers, {
          ...paginatedOriginal,
          ...patch,
          memberPermissions: merged.memberPermissions,
          userPermissions: merged.userPermissions,
        });
      }
    },
  },
  extraReducers: buildMemberExtraReducers,
});

export const memberReducer = memberSlice.reducer;

export const memberWebsocketActions: WebsocketEventActions[] = [
  {
    type: EmailEventTypes.EMAIL_ADDRESS_DELETED,
    action: memberSlice.actions.emailDeleted,
    channel: 'baseSocket',
  },
  {
    type: UserEventTypes.USER_UPDATED,
    action: memberSlice.actions.userUpdated,
    channel: 'baseSocket',
  },
];

registerMemberEffects();
